import { OperationIds } from '../swagger/operationIdEnum'

/** ATTENTION: Keep it alphabetic, please.
 *
 * The convention for naming is PascalCase name of the file with index signatures of
 * the OperationId of an endpoint being called to a string, which should have the value
 * of the operationId.
 */
export const useLoadingIds = (): {
  AccountContext: {
    [OperationIds.FetchStudentsForFamily]: string
    [OperationIds.FetchCountries]: string
    [OperationIds.FetchEnrollmentInvites]: string
  }
  AccountSettingsTab: {
    [OperationIds.EditBusinessEmail]: string
  }
  AssignChildrenCard: {
    [OperationIds.ValidatePotentialEnrollments]: string
  }
  AssignRole: {
    [OperationIds.AssignRole]: string
    [OperationIds.EditRole]: string
  }
  AuthFormCard: {
    [OperationIds.SendTokenForSignup]: string
  }
  AuthProvider: {
    [OperationIds.Logout]: string
  }
  BillingTab: {
    [OperationIds.FetchBilling]: string
    [OperationIds.FetchCountryCoordinatorLicensingDetails]: string
    [OperationIds.FetchCountryCoordinatorTotalBalance]: string
    [OperationIds.FetchLicensedDirectorBillingDetails]: string
  }
  Business: {
    [OperationIds.FetchBusinessContent]: string
    [OperationIds.FetchBusinessContentOptions]: string
  }
  BusinessContent: {
    [OperationIds.FetchContentDetail]: string
    [OperationIds.FetchUserNdaStatus]: string
  }
  ContentApi: {
    [OperationIds.FetchContentTagOptions]: string
    [OperationIds.FetchBusinessLearningPath]: string
    [OperationIds.FetchLearningCenterLearningPath]: string
  }
  CountryCoordinatorLicensingPaymentHistory: {
    [OperationIds.FetchLicensingInternationalPayments]: string
  }
  CommunityDetailsTab: {
    [OperationIds.FetchCommunity]: string
  }
  CommunityFamiliesTab: {
    [OperationIds.FetchAllFamilies]: string
  }
  CommunityProgramsTab: {
    [OperationIds.FetchAllPrograms]: string
  }
  CountryInformation: {
    [OperationIds.EditUserProfile]: string
  }
  DashboardTab: {
    [OperationIds.FetchMetrics]: string
    [OperationIds.FetchMetric]: string
  }
  Enrollments: {
    [OperationIds.UpdateLicensingEnrollments]: string
    [OperationIds.CreateLicensingEnrollment]: string
    [OperationIds.RemoveLicensingEnrollments]: string
  }
  EnrollmentSummaryCard: {
    [OperationIds.CompleteEnrollmentInvite]: string
  }
  EnrollmentsTable: {
    [OperationIds.UpdateStudentTutorAssignments]: string
    [OperationIds.UpdateEnrollmentStatus]: string
  }
  Events: {
    [OperationIds.FetchEventOptions]: string
    [OperationIds.FetchUsers]: string
    [OperationIds.FetchTimezones]: string
    [OperationIds.FetchEventRegistrants]: string
    [OperationIds.FetchEvents]: string
    [OperationIds.FetchEvent]: string
    [OperationIds.CreateRegistrant]: string
    [OperationIds.SearchPossibleEventOwners]: string
    [OperationIds.BulkReassignEvents]: string
  }
  Financials: {
    [OperationIds.FetchCommunityFinancials]: string
    [OperationIds.FetchDirectorFinancialDetails]: string
  }
  LeadershipTrainingEvents: {
    [OperationIds.FetchLeadershipTraining]: string
  }
  Families: {
    [OperationIds.FetchStudentsForFamily]: string
    [OperationIds.FetchFamilyTuitionBilling]: string
  }
  InviteContext: {
    [OperationIds.CompleteAgreement]: string
    [OperationIds.FetchInviteValidity]: string
    [OperationIds.Logout]: string
  }
  InviteForm: {
    [OperationIds.ConfirmTokenForSignup]: string
    [OperationIds.Logout]: string
    [OperationIds.SendTokenForSignup]: string
  }
  InviteFamilyFormCard: {
    [OperationIds.FetchUserProfile]: string
    [OperationIds.InviteFamily]: string
  }
  EnrollmentsInviteTable: {
    [OperationIds.FetchEnrollmentInvites]: string
    [OperationIds.ResendInvitation]: string
    [OperationIds.CancelInvitation]: string
    [OperationIds.DeclineInvitation]: string
  }
  Invoice: {
    [OperationIds.FetchInvoiceSignature]: string
    [OperationIds.UpdateInvoiceSignature]: string
  }
  InvoiceCard: {
    [OperationIds.SendTuitionInvoice]: string
  }
  InvoiceDetails: {
    [OperationIds.FetchTuitionInvoice]: string
  }
  LearningCenter: {
    [OperationIds.FetchContentDetail]: string
  }
  Login: {
    [OperationIds.Login]: string
  }
  Legal: {
    [OperationIds.FetchUserScreenings]: string
    [OperationIds.UpdateUserScreening]: string
  }
  Meta: {
    [OperationIds.FetchRoles]: string
    [OperationIds.FetchPermissions]: string
  }
  PaymentApi: {
    [OperationIds.FetchCountryCoordinatorLicensingDetails]: string
    [OperationIds.FetchInternationalLicensingFilterOptions]: string
    [OperationIds.FetchLicensingLcdPayments]: string
    [OperationIds.FetchLcdTotalOwed]: string
  }
  Notification: {
    [OperationIds.FetchNotifications]: string
    [OperationIds.FetchNotification]: string
    [OperationIds.CreateNotification]: string
    [OperationIds.EditNotification]: string
    [OperationIds.DeleteNotification]: string
    [OperationIds.FetchUserNotifications]: string
    [OperationIds.FetchRegions]: string
    [OperationIds.FetchRoles]: string
  }
  PaymentModal: {
    [OperationIds.FetchStates]: string
    [OperationIds.StartPayment]: string
    [OperationIds.AddInternationalLicensingPayment]: string
    [OperationIds.UpdateLicensingInternationalPayment]: string
  }
  ProfileTab: {
    [OperationIds.FetchStudentsForFamily]: string
    // TODO: Remove use in profile tab in lieu of UserContext
    [OperationIds.FetchUserCommunities]: string
    [OperationIds.EditUserProfile]: string
    [OperationIds.EditStudent]: OperationIds.EditStudent
  }
  ProgramDetails: {
    [OperationIds.FetchEnrollments]: string
    [OperationIds.FetchProgram]: string
  }
  Playlist: {
    [OperationIds.ClonePlaylist]: string
    [OperationIds.CreatePlaylist]: string
    [OperationIds.CreatePlaylistItems]: string
    [OperationIds.DeletePlaylist]: string
    [OperationIds.DeletePlaylistItem]: string
    [OperationIds.FetchPlaylist]: string
    [OperationIds.FetchSharePlaylist]: string
    [OperationIds.FetchUserPlaylists]: string
    [OperationIds.UpdatePlaylistName]: string
    [OperationIds.UpdatePlaylistItemsOrder]: string
    [OperationIds.SharePlaylist]: string
    [OperationIds.UnsharePlaylist]: string
  }
  Regions: {
    [OperationIds.FetchRegions]: string
    [OperationIds.FetchRegion]: string
    [OperationIds.FetchRegionContractingHierarchies]: string
    [OperationIds.FetchRegionFees]: string
    [OperationIds.FetchRegionOptions]: string
  }
  RegistrantsTable: {
    [OperationIds.UpdateRegistrant]: string
    [OperationIds.UpdateRegistrants]: string
  }
  TeamMemberCard: {
    [OperationIds.InviteTeamMember]: string
    [OperationIds.FetchInviteTeamMemberOptions]: string
  }
  TeamAgreementsTable: {
    [OperationIds.ResendInvitation]: string
  }
  TranscriptFormCard: {
    [OperationIds.CreateTranscript]: string
    [OperationIds.UpdateTranscript]: string
    [OperationIds.FetchGradingScales]: string
    [OperationIds.SendTranscriptToParchment]: string
    [OperationIds.MarkParchmentAsSent]: string
    [OperationIds.MarkParchmentAsUnsent]: string
    [OperationIds.UpdateTranscriptOnHoldStatus]: string
  }
  UpdateChildrenInfoCard: {
    [OperationIds.EditStudent]: string
  }
  UpdateParentInfoCard: {
    [OperationIds.EditUserProfile]: string
  }
  UseFetchTranscripts: {
    [OperationIds.FetchTranscripts]: string
    [OperationIds.FetchGradingScales]: string
  }
  UseDeleteTranscript: {
    [OperationIds.DeleteTranscript]: string
  }
  UserAccounts: {
    [OperationIds.FetchUsers]: string
    [OperationIds.FetchRoles]: string
    [OperationIds.FetchUserAccountRoles]: string
    [OperationIds.FetchUser]: string
    [OperationIds.FetchBilling]: string
    [OperationIds.FetchPaymentOptions]: string
    [OperationIds.FetchUserProfile]: string
  }
  UserAccountLicensingReport: { [OperationIds.FetchUser]: string }
  UserContext: {
    [OperationIds.FetchUserCommunities]: string
  }
} => {
  return {
    AccountContext: {
      [OperationIds.FetchStudentsForFamily]:
        OperationIds.FetchStudentsForFamily,
      [OperationIds.FetchCountries]: OperationIds.FetchCountries,
      [OperationIds.FetchEnrollmentInvites]:
        OperationIds.FetchEnrollmentInvites,
    },
    AccountSettingsTab: {
      [OperationIds.EditBusinessEmail]: OperationIds.EditBusinessEmail,
    },
    AssignChildrenCard: {
      [OperationIds.ValidatePotentialEnrollments]:
        OperationIds.ValidatePotentialEnrollments,
    },
    AssignRole: {
      [OperationIds.AssignRole]: OperationIds.AssignRole,
      [OperationIds.EditRole]: OperationIds.EditRole,
    },
    AuthFormCard: {
      [OperationIds.SendTokenForSignup]: OperationIds.SendTokenForSignup,
    },
    AuthProvider: {
      [OperationIds.Logout]: OperationIds.Logout,
    },
    BillingTab: {
      [OperationIds.FetchBilling]: OperationIds.FetchBilling,
      [OperationIds.FetchCountryCoordinatorLicensingDetails]:
        OperationIds.FetchCountryCoordinatorLicensingDetails,
      [OperationIds.FetchCountryCoordinatorTotalBalance]:
        OperationIds.FetchCountryCoordinatorTotalBalance,
      [OperationIds.FetchLicensedDirectorBillingDetails]:
        OperationIds.FetchLicensedDirectorBillingDetails,
    },
    Business: {
      [OperationIds.FetchBusinessContent]: OperationIds.FetchBusinessContent,
      [OperationIds.FetchBusinessContentOptions]:
        OperationIds.FetchBusinessContentOptions,
    },
    BusinessContent: {
      [OperationIds.FetchContentDetail]: OperationIds.FetchContentDetail,
      [OperationIds.FetchUserNdaStatus]: OperationIds.FetchUserNdaStatus,
    },
    ContentApi: {
      [OperationIds.FetchContentTagOptions]:
        OperationIds.FetchContentTagOptions,
      [OperationIds.FetchBusinessLearningPath]:
        OperationIds.FetchBusinessLearningPath,
      [OperationIds.FetchLearningCenterLearningPath]:
        OperationIds.FetchLearningCenterLearningPath,
    },
    CountryCoordinatorLicensingPaymentHistory: {
      [OperationIds.FetchLicensingInternationalPayments]:
        OperationIds.FetchLicensingInternationalPayments,
    },
    CommunityDetailsTab: {
      [OperationIds.FetchCommunity]: OperationIds.FetchCommunity,
    },
    CommunityFamiliesTab: {
      [OperationIds.FetchAllFamilies]: OperationIds.FetchAllFamilies,
    },
    CommunityProgramsTab: {
      [OperationIds.FetchAllPrograms]: OperationIds.FetchAllPrograms,
    },
    CountryInformation: {
      [OperationIds.EditUserProfile]: OperationIds.EditUserProfile,
    },
    DashboardTab: {
      [OperationIds.FetchMetrics]: OperationIds.FetchMetrics,
      [OperationIds.FetchMetric]: OperationIds.FetchMetric,
    },
    Enrollments: {
      [OperationIds.UpdateLicensingEnrollments]:
        OperationIds.UpdateLicensingEnrollments,
      [OperationIds.CreateLicensingEnrollment]:
        OperationIds.CreateLicensingEnrollment,
      [OperationIds.RemoveLicensingEnrollments]:
        OperationIds.RemoveLicensingEnrollments,
    },
    EnrollmentSummaryCard: {
      [OperationIds.CompleteEnrollmentInvite]:
        OperationIds.CompleteEnrollmentInvite,
    },
    EnrollmentsTable: {
      [OperationIds.UpdateStudentTutorAssignments]:
        OperationIds.UpdateStudentTutorAssignments,
      [OperationIds.UpdateEnrollmentStatus]:
        OperationIds.UpdateEnrollmentStatus,
    },
    Events: {
      [OperationIds.FetchEventOptions]: OperationIds.FetchEventOptions,
      [OperationIds.FetchUsers]: OperationIds.FetchUsers,
      [OperationIds.FetchTimezones]: OperationIds.FetchTimezones,
      [OperationIds.FetchEventRegistrants]: OperationIds.FetchEventRegistrants,
      [OperationIds.FetchEvents]: OperationIds.FetchEvents,
      [OperationIds.FetchEvent]: OperationIds.FetchEvent,
      [OperationIds.CreateRegistrant]: OperationIds.CreateRegistrant,
      [OperationIds.SearchPossibleEventOwners]:
        OperationIds.SearchPossibleEventOwners,
      [OperationIds.BulkReassignEvents]: OperationIds.BulkReassignEvents,
    },
    Financials: {
      [OperationIds.FetchCommunityFinancials]:
        OperationIds.FetchCommunityFinancials,
      [OperationIds.FetchDirectorFinancialDetails]:
        OperationIds.FetchDirectorFinancialDetails,
    },
    LeadershipTrainingEvents: {
      [OperationIds.FetchLeadershipTraining]:
        OperationIds.FetchLeadershipTraining,
    },
    Families: {
      [OperationIds.FetchStudentsForFamily]:
        OperationIds.FetchStudentsForFamily,
      [OperationIds.FetchFamilyTuitionBilling]:
        OperationIds.FetchFamilyTuitionBilling,
    },
    InviteContext: {
      [OperationIds.CompleteAgreement]: OperationIds.CompleteAgreement,
      [OperationIds.FetchInviteValidity]: OperationIds.FetchInviteValidity,
      [OperationIds.Logout]: OperationIds.Logout,
    },
    InviteForm: {
      [OperationIds.SendTokenForSignup]: OperationIds.SendTokenForSignup,
      [OperationIds.ConfirmTokenForSignup]: OperationIds.ConfirmTokenForSignup,
      [OperationIds.Logout]: OperationIds.Logout,
    },
    InviteFamilyFormCard: {
      [OperationIds.FetchUserProfile]: OperationIds.FetchUserProfile,
      [OperationIds.InviteFamily]: OperationIds.InviteFamily,
    },
    EnrollmentsInviteTable: {
      [OperationIds.FetchEnrollmentInvites]:
        OperationIds.FetchEnrollmentInvites,
      [OperationIds.ResendInvitation]: OperationIds.ResendInvitation,
      [OperationIds.CancelInvitation]: OperationIds.CancelInvitation,
      [OperationIds.DeclineInvitation]: OperationIds.DeclineInvitation,
    },
    Invoice: {
      [OperationIds.FetchInvoiceSignature]: OperationIds.FetchInvoiceSignature,
      [OperationIds.UpdateInvoiceSignature]:
        OperationIds.UpdateInvoiceSignature,
    },
    InvoiceCard: {
      [OperationIds.SendTuitionInvoice]: OperationIds.SendTuitionInvoice,
    },
    InvoiceDetails: {
      [OperationIds.FetchTuitionInvoice]: OperationIds.FetchTuitionInvoice,
    },
    LearningCenter: {
      [OperationIds.FetchContentDetail]: OperationIds.FetchContentDetail,
    },
    Legal: {
      [OperationIds.FetchUserScreenings]: OperationIds.FetchUserScreenings,
      [OperationIds.UpdateUserScreening]: OperationIds.UpdateUserScreening,
    },
    Login: {
      [OperationIds.Login]: OperationIds.Login,
    },
    Meta: {
      [OperationIds.FetchRoles]: OperationIds.FetchRoles,
      [OperationIds.FetchPermissions]: OperationIds.FetchPermissions,
    },
    PaymentApi: {
      [OperationIds.FetchCountryCoordinatorLicensingDetails]:
        OperationIds.FetchCountryCoordinatorLicensingDetails,
      [OperationIds.FetchInternationalLicensingFilterOptions]:
        OperationIds.FetchInternationalLicensingFilterOptions,
      [OperationIds.FetchLicensingLcdPayments]:
        OperationIds.FetchLicensingLcdPayments,
      [OperationIds.FetchLcdTotalOwed]: OperationIds.FetchLcdTotalOwed,
    },
    Notification: {
      [OperationIds.FetchNotifications]: OperationIds.FetchNotifications,
      [OperationIds.FetchNotification]: OperationIds.FetchNotification,
      [OperationIds.CreateNotification]: OperationIds.CreateNotification,
      [OperationIds.DeleteNotification]: OperationIds.DeleteNotification,
      [OperationIds.EditNotification]: OperationIds.EditNotification,
      [OperationIds.FetchUserNotifications]:
        OperationIds.FetchUserNotifications,
      [OperationIds.FetchRegions]: OperationIds.FetchRegions,
      [OperationIds.FetchRoles]: OperationIds.FetchRoles,
    },
    PaymentModal: {
      [OperationIds.FetchStates]: OperationIds.FetchStates,
      [OperationIds.StartPayment]: OperationIds.StartPayment,
      [OperationIds.AddInternationalLicensingPayment]:
        OperationIds.AddInternationalLicensingPayment,
      [OperationIds.UpdateLicensingInternationalPayment]:
        OperationIds.UpdateLicensingInternationalPayment,
    },
    ProfileTab: {
      [OperationIds.FetchStudentsForFamily]:
        OperationIds.FetchStudentsForFamily,
      [OperationIds.FetchUserCommunities]: OperationIds.FetchUserCommunities,
      [OperationIds.EditUserProfile]: OperationIds.EditUserProfile,
      [OperationIds.EditStudent]: OperationIds.EditStudent,
    },
    ProgramDetails: {
      [OperationIds.FetchEnrollments]: OperationIds.FetchEnrollments,
      [OperationIds.FetchProgram]: OperationIds.FetchProgram,
    },
    Playlist: {
      [OperationIds.ClonePlaylist]: OperationIds.ClonePlaylist,
      [OperationIds.CreatePlaylist]: OperationIds.CreatePlaylist,
      [OperationIds.CreatePlaylistItems]: OperationIds.CreatePlaylistItems,
      [OperationIds.DeletePlaylist]: OperationIds.DeletePlaylist,
      [OperationIds.DeletePlaylistItem]: OperationIds.DeletePlaylistItem,
      [OperationIds.FetchSharePlaylist]: OperationIds.FetchSharePlaylist,
      [OperationIds.FetchPlaylist]: OperationIds.FetchPlaylist,
      [OperationIds.FetchUserPlaylists]: OperationIds.FetchUserPlaylists,
      [OperationIds.UnsharePlaylist]: OperationIds.UnsharePlaylist,
      [OperationIds.UpdatePlaylistName]: OperationIds.UpdatePlaylistName,
      [OperationIds.SharePlaylist]: OperationIds.SharePlaylist,
      [OperationIds.UpdatePlaylistItemsOrder]:
        OperationIds.UpdatePlaylistItemsOrder,
    },
    Regions: {
      [OperationIds.FetchRegions]: OperationIds.FetchRegions,
      [OperationIds.FetchRegion]: OperationIds.FetchRegion,
      [OperationIds.FetchRegionContractingHierarchies]:
        OperationIds.FetchRegionContractingHierarchies,
      [OperationIds.FetchRegionFees]: OperationIds.FetchRegionFees,
      [OperationIds.FetchRegionOptions]: OperationIds.FetchRegionOptions,
    },
    RegistrantsTable: {
      [OperationIds.UpdateRegistrant]: OperationIds.UpdateRegistrant,
      [OperationIds.UpdateRegistrants]: OperationIds.UpdateRegistrants,
    },
    TeamMemberCard: {
      [OperationIds.InviteTeamMember]: OperationIds.InviteTeamMember,
      [OperationIds.FetchInviteTeamMemberOptions]:
        OperationIds.FetchInviteTeamMemberOptions,
    },
    TeamAgreementsTable: {
      [OperationIds.ResendInvitation]: OperationIds.ResendInvitation,
    },
    TranscriptFormCard: {
      [OperationIds.CreateTranscript]: OperationIds.CreateTranscript,
      [OperationIds.UpdateTranscript]: OperationIds.UpdateTranscript,
      [OperationIds.FetchGradingScales]: OperationIds.FetchGradingScales,
      [OperationIds.SendTranscriptToParchment]:
        OperationIds.SendTranscriptToParchment,
      [OperationIds.MarkParchmentAsSent]: OperationIds.MarkParchmentAsSent,
      [OperationIds.MarkParchmentAsUnsent]: OperationIds.MarkParchmentAsUnsent,
      [OperationIds.UpdateTranscriptOnHoldStatus]:
        OperationIds.UpdateTranscriptOnHoldStatus,
    },
    UpdateChildrenInfoCard: {
      [OperationIds.EditStudent]: OperationIds.EditStudent,
    },
    UpdateParentInfoCard: {
      [OperationIds.EditUserProfile]: OperationIds.EditUserProfile,
    },
    UseFetchTranscripts: {
      [OperationIds.FetchTranscripts]: OperationIds.FetchTranscripts,
      [OperationIds.FetchGradingScales]: OperationIds.FetchGradingScales,
    },
    UseDeleteTranscript: {
      [OperationIds.DeleteTranscript]: OperationIds.DeleteTranscript,
    },
    UserAccounts: {
      [OperationIds.FetchUsers]: OperationIds.FetchUsers,
      [OperationIds.FetchRoles]: OperationIds.FetchRoles,
      [OperationIds.FetchUserAccountRoles]: OperationIds.FetchUserAccountRoles,
      [OperationIds.FetchUser]: OperationIds.FetchUser,
      [OperationIds.FetchBilling]: OperationIds.FetchBilling,
      [OperationIds.FetchPaymentOptions]: OperationIds.FetchPaymentOptions,
      [OperationIds.FetchUserProfile]: OperationIds.FetchUserProfile,
    },
    UserAccountLicensingReport: {
      [OperationIds.FetchUser]: OperationIds.FetchUser,
    },
    UserContext: {
      [OperationIds.FetchUserCommunities]: OperationIds.FetchUserCommunities,
    },
  }
}
